import { useState, useEffect, useCallback } from 'react';
import programApi from './programApi';

export const useProgramTypes = (tenantId, shouldFetch = true) => {
  const [programTypes, setProgramTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchProgramTypes = useCallback(async () => {
    if (!tenantId || !shouldFetch) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await programApi.getProgramTypes(tenantId);
      setProgramTypes(response.data.programTypes || []);
    } catch (err) {
      console.error('Error fetching program types:', err);
      setError(
        err.response?.data?.message ||
        'Unable to load program types. Please try again.'
      );
      setProgramTypes([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, shouldFetch]);

  const createOrUpdateType = async (formData, editingType = null) => {
    try {
      setSaving(true);
      setError(null);

      const data = {
        tenantId,
        code: formData.code,
        name: formData.name,
        description: formData.description,
        status: formData.status
      };

      let response;
      if (editingType) {
        response = await programApi.updateProgramType(editingType._id, data);
      } else {
        response = await programApi.createProgramType(data);
      }

      await fetchProgramTypes(); // Refresh the list
      return response.data;
    } catch (err) {
      console.error('Error saving program type:', err);
      const errorMessage = err.response?.data?.message || 'Error saving program type';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchProgramTypes();
  }, [fetchProgramTypes]);

  return {
    programTypes,
    loading,
    error,
    saving,
    fetchProgramTypes,
    createOrUpdateType
  };
};

export default useProgramTypes;